// The interaction will listen to events on sources which are instances of ol.source.Cluster
// and show each feature of the e.g. clicked cluster around the clustered feature.
import { detectMobile } from "@config/browser";
import Interaction from 'ol/interaction/Interaction';
import Cluster from 'ol/source/Cluster';
import VectorLayer  from 'ol/layer/Vector';
import VectorSource  from 'ol/source/Vector';
import {Stroke, Style} from 'ol/style';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import MapBrowserEventType from 'ol/MapBrowserEventType';
import {createEmpty, extend} from 'ol/extent';

export const SPIDERFIER_PROPERTY = 'spiderfy';
export const SPIDERFIER_NS = 'ClusterSpiderfier.';

export function spiderfierSelectedLayerProperty() {
  return SPIDERFIER_NS + 'selected';
}
export function spiderfierOverlayLegLayerProperty() {
  return SPIDERFIER_NS + 'overlayLegLayer';
}
export function spiderfierOverlayLayerProperty() {
  return SPIDERFIER_NS + 'overlayLayer';
}


/**
 * @constructor
 * @extends {ol.interaction.Pointer}
 */
class ClusterSpiderfier extends Interaction {
  /**
   * @param {Options} [opt_options] Options.
   */
  constructor(opt_options) {
    const options = opt_options ? opt_options : {};
    super(options);

    this.twoPi = Math.PI * 2
    // this.radius = options.radius || 50;
    this.circleFootSeparation = 50; //23; // related to circumference of circle
    this.circleStartAngle = this.twoPi / 12;
    this.spiralFootSeparation = 53; //26; // related to size of spiral (experiment!)
    this.spiralLengthStart = 11; // ditto
    this.spiralLengthFactor = 4; // ditto
    this.displayGeometry = options.displayGeometry || 'circle';
    this.style = options.style;
    this.ns = SPIDERFIER_NS;
    this.selectedClusters = [];

    this.zoomToBoundsDuration = options.zoomToBoundsDuration || 1000;

    this.zoomToBoundsPaddingDefault = options.zoomToBoundsPaddingDefault || (detectMobile() ? 80 : 150);

    this.zoomToBoundsPadding = options.zoomToBoundsPadding
      || [this.zoomToBoundsPaddingDefault, this.zoomToBoundsPaddingDefault, this.zoomToBoundsPaddingDefault, this.zoomToBoundsPaddingDefault];

    this.zoomToBoundsPaddingReferenceElement = options.zoomToBoundsPaddingReferenceElement || null;


    // Whether the default behaviour of clicking on a cluster is to zoom into it.
    this.zoomToBoundsOnClick = options.zoomToBoundsOnClick || true;
    // When you click a cluster at the bottom zoom level we spiderfy it so you can see all of its markers.
    this.spiderfyOnMaxZoom = options.spiderfyOnMaxZoom || true;

    this.prop_spiderfier = SPIDERFIER_PROPERTY;
  }

  /**
   * Handles the {@link module:ol/MapBrowserEvent map browser event} (if it was a
   * doubleclick) and eventually zooms the map.
   * @param {import("../MapBrowserEvent.js").default} mapBrowserEvent Map browser event.
   * @return {boolean} `false` to stop event propagation.
   */
  handleEvent(mapBrowserEvent) {
    const map = mapBrowserEvent.map;
    var self = this;
    const pixel = mapBrowserEvent.pixel;
    const hit = map.hasFeatureAtPixel(pixel);

    if (mapBrowserEvent.type == MapBrowserEventType.POINTERMOVE) {
      var target = map.getTargetElement();
      target.style.cursor = hit ? 'pointer' : '';
      return true;
    }

    if (mapBrowserEvent.type != MapBrowserEventType.CLICK) {
      return true;
    }

    if(!hit){
      this._clearMarkers();
    }

    const zoomOrSpiderfy = map.forEachFeatureAtPixel(mapBrowserEvent.pixel, (feature, layer) => {
      // check if feature is coming from cluster source
      let source = layer ? layer.getSource() : null;
      if (source && source instanceof Cluster) {
        let map = this.map_,
            spiderfyOnMaxZoom = this.spiderfyOnMaxZoom,
            zoomToBoundsOnClick = this.zoomToBoundsOnClick;

        //Zoom on cluster click or spiderfy if we are at the lowest level
        if (spiderfyOnMaxZoom || zoomToBoundsOnClick) {
            this._zoomOrSpiderfy(feature, layer);
            feature.set(this.prop_spiderfier, feature.get('features') ? feature.get('features').length>1 : false);
            return feature;
        }

      } else if (layer && layer.get(spiderfierSelectedLayerProperty()) === true
        || !layer && feature.get(spiderfierSelectedLayerProperty()) ) {
        self._close(feature);
      }else{
        feature.set(this.prop_spiderfier, feature.get('features') ? feature.get('features').length>1 : false);
      }
    });
    return !!!zoomOrSpiderfy;
  }

  /**
   * Remove the interaction from its current map, if any,  and attach it to a new
   * map, if any. Pass `null` to just remove the interaction from the current map.
   * @param {ol.Map} map Map.
   * @api stable
   */
  setMap(map) {
    this.map_ = map;
    if (map && map.getView()) {
      map.getView().on('change:resolution', (evt) => {
        this._clearMarkers();
      })
    }
  }

  _clearMarkers() {
    for (let i = this.selectedClusters.length-1; i >= 0; i--){
      // close spider when zoom level changes
      let features = this.selectedClusters[i].getSource().getFeatures();
      if(features.length>0) {
        for (let f = features.length - 1; f >= 0; f--) {
          this._close(features[f]);
        }
      }
    }
    this.selectedClusters = [];
  }

  _zoomOrSpiderfy(feature, layer) {
    var cluster = layer,
      clusterFeatures = feature.get('features') || [],
      map = this.map_,
      mapView = map.getView();

    var _zoom = Math.ceil(mapView.getZoom()),
        _maxZoom = Math.ceil(mapView.getMaxZoom()),
        _minZoom = Math.floor(mapView.getMinZoom());

    if (_zoom === _maxZoom &&
      clusterFeatures.length > 1 &&
      this.spiderfyOnMaxZoom) {
      //   // All child markers are contained in a single cluster from this._maxZoom to this cluster.
      feature.set('originLayer', layer);
      this._open(feature);
      return 1;
    } else if (this.zoomToBoundsOnClick) {
      this._zoomToClusterBounds(feature);
    }
    return 0;
  }

  _zoomToClusterBounds(feature){
    const clusterFeatures = feature.get('features');
    if(clusterFeatures && clusterFeatures.length>1){
      let featExtent = createEmpty();
      for (let i = 0; i < clusterFeatures.length; i++) {
          extend(featExtent, clusterFeatures[i].getGeometry().getExtent());
      }
      this.map_.getView().fit(featExtent, {duration: this.zoomToBoundsDuration, padding: this._getPaddingView()});
    }
  }

  _getPaddingView() {
    let padding = this.zoomToBoundsPadding;
    let elem = this._getPaddingElement();
    if(elem) {
      if(padding) {
        // top, right, bottom and left padding.
        return [
          padding[0],
          elem.clientLeft == 0 ? padding[1] : (elem.clientWidth + this.zoomToBoundsPaddingDefault),
          padding[2],
          elem.clientLeft > 0 ? padding[3] : (elem.clientWidth + this.zoomToBoundsPaddingDefault),
        ]
      }

    }
    return padding;
  }

  _getPaddingElement() {
    const elem = this.zoomToBoundsPaddingReferenceElement;
    if (elem !== undefined) {
      return typeof elem === 'string'
        ? document.querySelector(elem) //getElementBySE(elem)
        : elem;
    } else {
      return null;
    }
  }

  getFeatures() {
    return []; //this.featureOverlay_.getSource().getFeatures();
  }

  _open(feature) {
    var map = this.map_;
    var clusterFeatures = feature.get('features');
    // Lists with one feature shouldn't trigger the opening.
    if (clusterFeatures.length == 1) {
      return;
    }

    // Create a new layer for the selected item
    var selectedLayer = new VectorLayer({
      source: new VectorSource({
        opacity: 0.5
      }),
      updateWhileAnimating: true,
      updateWhileInteracting: true
    });
    selectedLayer.set(spiderfierSelectedLayerProperty(), true);
    selectedLayer.setMap(map);
    this.selectedClusters.push(selectedLayer);

    // Create a new overlay layer for leg
    var overlayLegLayer = new VectorLayer({
      source: new VectorSource({
        useSpatialIndex: true
      }),
      updateWhileAnimating: true,
      updateWhileInteracting: true,
      style: this._getStyleLeg(),
    });
    overlayLegLayer.set(spiderfierOverlayLegLayerProperty(), true);
    overlayLegLayer.setMap(map);

    // Create a new overlay layer for markers
    var overlayLayer = new VectorLayer({
      source: new VectorSource({
        useSpatialIndex: true
      }),
      updateWhileAnimating: true,
      updateWhileInteracting: true
    });
    overlayLayer.set(spiderfierOverlayLayerProperty(), true);
    overlayLayer.setMap(map);

    // Add cluster feature to selected layer
    var selectedFeature = feature.clone();
    selectedFeature.set(this.prop_spiderfier, true);
    selectedFeature.set(this.ns + 'originFeature', feature);

    // Keep a reference to the layers
    selectedFeature.set(spiderfierOverlayLayerProperty(), overlayLayer);
    selectedFeature.set(spiderfierOverlayLegLayerProperty(), overlayLegLayer);
    selectedFeature.set(spiderfierSelectedLayerProperty(), selectedLayer);

    var sourceLeg = overlayLegLayer.getSource();
    var source = overlayLayer.getSource();
    var originLayer = feature.get('originLayer');
    if (!selectedLayer.get('hasOwnStyle') && originLayer.getStyle()) {
      selectedLayer.setStyle(originLayer.getStyle());
      selectedLayer.setOpacity(0.5);
    }

    // Add selected feature to layer
    selectedLayer.getSource().addFeature(selectedFeature);

    // MARKERS OVERLAYER
    // TODO: merge these lines together
    // Set default style for overlay items (if available)
    if (typeof this.style !== 'undefined') {
      overlayLayer.set('hasOwnStyle', true);
      overlayLayer.setStyle(this.style);
    }

    // If our layer hasn't explicitly set a style inherit the one from the origin
    // layer.
    if (!overlayLayer.get('hasOwnStyle') && originLayer.getStyle()) {
      overlayLayer.setStyle(originLayer.getStyle());
    }
    overlayLayer.set('originLayer', originLayer);

    // add individual features of the cluster
    clusterFeatures.forEach((f) => {
      var lf = f.clone();
      lf.setStyle(overlayLegLayer.getStyle());
      lf.set(this.prop_spiderfier, true);
      sourceLeg.addFeature(lf);
      var cf = f.clone();
      cf.setId(f.getId());
      cf.set(this.prop_spiderfier, true);
      cf.set('originLayer', originLayer);
      cf.set('originFeature', feature);
      source.addFeature(cf);
    });

    // hide cluster feature in origin layer (transparency won't work nicely otherwise)
    feature.setStyle(new Style());
    let featuresLeg = sourceLeg.getFeatures();
    let features = source.getFeatures();
    switch (this.displayGeometry) {
      case 'spiral':
        this._arrangeSpiral(feature, featuresLeg, features);
        break;

      case 'circle':
      default:
        this._arrangeCircle(feature, featuresLeg, features);
        break;
    }
  }

  _close(selectedFeature) {
    let map = this.map_;
    let selectedLayer = selectedFeature.get(spiderfierSelectedLayerProperty());
    let overlayLayer = selectedFeature.get(spiderfierOverlayLayerProperty());
    let overlayLegLayer = selectedFeature.get(spiderfierOverlayLegLayerProperty());
    let selectedSource = selectedLayer.getSource();
    let overlaySource = overlayLayer.getSource();
    let overlayLegSource = overlayLegLayer.getSource();
    // let cluster = this.selectedClusters.filter(layer => layer.getSource().getFeatures().indexOf(selectedFeature) != -1);
    let idxCluster = this.selectedClusters.reduce(function(result, layer, idx) {
      if (layer.getSource().getFeatures().indexOf(selectedFeature) != -1)
        result.push(idx);
      return result;
    }, []);

    // clear overlay
    overlayLegSource.clear();
    overlayLegLayer.setSource(null);
    overlaySource.clear();
    overlayLayer.setSource(null);

    // unhide original cluster feature
    let originFeature = selectedFeature.get(this.ns + 'originFeature');
    originFeature.setStyle(null);
    selectedSource.clear();

    map.removeLayer(selectedLayer);
    map.removeLayer(overlayLegLayer);
    map.removeLayer(overlayLayer);

    // let index = this.selectedClusters.indexOf(selectedLayer);
    // this.selectedClusters.splice(index, 1);
    // let indexes = this.selectedClusters.reduce(function(a, e, i) {
    //   if (e === selectedLayer)
    //       a.push(i);
    //   return a;
    // }, []);

    // if(indexes.length == 1) {
    //   this.selectedClusters.splice(indexes[0], 1);
    // }
    for (let i = idxCluster.length-1; i >= 0; i--){
      this.selectedClusters.splice(idxCluster[i], 1);
    }

  }

  _getGeometryCenterCoordinates(geometry) {
    if (geometry instanceof Point) {
      return geometry.getCoordinates();
    }
  }

  _arrangeSpiral(centerFeature, featuresLeg, features) {
    var center_coord, center_pixel, legLength, angle;
    var map = this.map_;

    center_coord = this._getGeometryCenterCoordinates(centerFeature.getGeometry());
    center_pixel = map.getPixelFromCoordinate(center_coord);
    legLength = this.spiralLengthStart;

    const coord = (angle, legLength) => map.getCoordinateFromPixel([
      center_pixel[0] + legLength * Math.cos(angle),
      center_pixel[1] + legLength * Math.sin(angle),
    ]);

    angle = 0;
    features.forEach((f, i) => {
      angle += this.spiralFootSeparation / legLength + i * 0.0005;
      f.setGeometry(new Point(coord(angle, legLength)));
      featuresLeg[i].setGeometry(new LineString([center_coord, coord(angle, legLength)]));
      legLength += this.twoPi * this.spiralLengthFactor / angle;
    })
  }

  _arrangeCircle(centerFeature, featuresLeg, features) {
    let center_coord, center_pixel, count, circumference, legLength, angleStep;
    let map = this.map_;

    count = features.length;
    center_coord = this._getGeometryCenterCoordinates(centerFeature.getGeometry());
    center_pixel = map.getPixelFromCoordinate(center_coord);

    const coord = (angle, legLength) => map.getCoordinateFromPixel([
      center_pixel[0] + Math.cos(angle) * legLength,
      center_pixel[1] + Math.sin(angle) * legLength
    ]);

    circumference = this.circleFootSeparation * (2 + count);
    legLength = circumference / this.twoPi;  // = radius from circumference
    angleStep = this.twoPi / count;

    const angle = (i) => this.circleStartAngle + i * angleStep;

    features.forEach((f, i) => {
      f.setGeometry(new Point(coord(angle(i), legLength)));
      featuresLeg[i].setGeometry(new LineString([center_coord, coord(angle(i), legLength)]));
    });
  }

  _getStyleLeg(){
    return new Style({
      stroke: new Stroke({
        color: '#222',
        width: 1.5,
        opacity: 0.5
      }),
    });
  }

};



export default ClusterSpiderfier;